import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { ArrowLeft, Send, AlertCircle } from 'lucide-react';
import { formsApi } from '../api/api';
import { Form } from '../types/form';
import AVCheckField from '../components/form-fields/AVCheckField';
import ConnectionCheckField from '../components/form-fields/ConnectionCheckField';
import SchedulerField from '../components/form-fields/SchedulerField';
import { shouldShowSchedulerField } from '../utils/conditionalLogic';
import { toast } from 'sonner';

const AnonymousFormFillForm: React.FC = () => {
  const { accessToken, formId } = useParams<{ accessToken: string; formId: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [showUserInfoForm, setShowUserInfoForm] = useState(true);
  const [userInfo, setUserInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    institute: '',
    role: '',
    country: 'TR',
    region: '',
    city: ''
  });
  const [locationData, setLocationData] = useState<{
    countries: [string, string][];
    regions: string[];
    cities_by_region: Record<string, string[]>;
  } | null>(null);

  useEffect(() => {
    const fetchForm = async () => {
      if (!accessToken || !formId) {
        setError('Invalid access parameters');
        setLoading(false);
        return;
      }

      try {
        const accessResponse = await formsApi.get(`/anonymous/${accessToken}/forms/`);
        const accessibleForm = accessResponse.data.forms.find((f: Form) => f.id === formId);
        
        if (!accessibleForm) {
          setError('You do not have access to this form');
          setLoading(false);
          return;
        }

        setForm(accessibleForm);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load form');
      } finally {
        setLoading(false);
      }
    };

    const fetchLocationData = async () => {
      try {
        const response = await formsApi.get('/location-data/');
        setLocationData(response.data);
      } catch (err) {
        console.error('Failed to load location data:', err);
      }
    };

    fetchForm();
    fetchLocationData();
  }, [accessToken, formId]);

  const handleInputChange = (fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleUserInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userInfo.first_name || !userInfo.last_name || !userInfo.email || !userInfo.phone || !userInfo.institute || !userInfo.role || !userInfo.country || !userInfo.region || !userInfo.city) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userInfo.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    const phoneRegex = /^[\d\s\-+()]+$/;
    if (!phoneRegex.test(userInfo.phone)) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setShowUserInfoForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !accessToken) return;

    setSubmitting(true);
    try {
      await formsApi.post(`/anonymous/${accessToken}/forms/${form.id}/submit/`, {
        values: formData,
        user_info: userInfo,
        institute: userInfo.institute,
        role: userInfo.role,
        country: userInfo.country,
        region: userInfo.region,
        city: userInfo.city
      });

      toast.success('Form submitted successfully!');
      navigate(`/anonymous/${accessToken}`);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const isImageField = (field: any): field is { field_type: 'image'; options: { layout?: string; [key: string]: any } } => {
    return field.field_type === 'image';
  };

  const renderAboveTitleImages = () => {
    if (!form) return null;
    
    const aboveTitleImages = form.fields.filter(field => 
      isImageField(field) && field.options?.aboveTitle === true
    );
    
    if (aboveTitleImages.length === 0) return null;
    
    return (
      <div className="mb-8 space-y-4">
        {aboveTitleImages.map((field) => renderImageField(field))}
      </div>
    );
  };

  const renderImageField = (field: any) => {
    const getImageSizeClass = (size: string) => {
      switch (size) {
        case 'small': return 'max-w-[150px]';
        case 'medium': return 'max-w-[300px]';
        case 'large': return 'max-w-[500px]';
        case 'full': return 'w-full';
        default: return 'max-w-[300px]';
      }
    };
    
    const getImageMaxHeight = (size: string) => {
      switch (size) {
        case 'small': return '150px';
        case 'medium': return '300px';
        case 'large': return '400px';
        case 'full': return '500px';
        default: return '400px';
      }
    };

    if (field.layout_config?.layout === 'side-by-side') {
      return (
        <div key={field.id} className="flex justify-between items-start w-full">
          {(field.options?.imageUrl || field.options?.imageFile) && (
            <div className={`${getImageSizeClass(field.options?.imageSize || 'medium')} flex justify-start`}>
              <img
                src={field.options.imageFile || field.options.imageUrl}
                alt={field.options?.imageAlt || 'Left image'}
                className={`h-auto rounded object-cover ${field.options?.showBorder !== false ? 'border' : ''}`}
                style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.style.display = 'none';
                }}
              />
            </div>
          )}
          {(field.options?.imageUrl2 || field.options?.imageFile2) && (
            <div className={`${getImageSizeClass(field.options?.imageSize || 'medium')} flex justify-end`}>
              <img
                src={field.options.imageFile2 || field.options.imageUrl2}
                alt={field.options?.imageAlt2 || 'Right image'}
                className={`h-auto rounded object-cover ${field.options?.showBorder !== false ? 'border' : ''}`}
                style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      );
    }

    return (field.options?.imageUrl || field.options?.imageFile) ? (
      <div key={field.id} className={`${getImageSizeClass(field.options?.imageSize || 'medium')} ${field.options?.aboveTitle ? '' : 'mx-auto'}`}>
        <img
          src={field.options.imageFile || field.options.imageUrl}
          alt={field.options?.imageAlt || 'Form image'}
          className={`${field.options?.aboveTitle ? 'h-auto' : 'w-full h-auto'} rounded ${field.options?.showBorder !== false ? 'border' : ''}`}
          style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
          onError={(e) => {
            const img = e.target as HTMLImageElement;
            img.style.display = 'none';
          }}
        />
      </div>
    ) : null;
  };

  const renderField = (field: any) => {
    const value = formData[field.id] || '';

    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <Input
            type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.is_required}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.is_required}
          />
        );

      case 'textarea':
        return (
          <Textarea
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.is_required}
            rows={4}
          />
        );

      case 'dropdown':
        return (
          <Select value={value} onValueChange={(val) => handleInputChange(field.id, val)}>
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option: string, index: number) => (
                <SelectItem key={index} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'radio':
        const radioLayout = field.layout_config?.layout || 'vertical';
        const radioLabelBelow = field.layout_config?.labelBelow || false;
        const radioEvenlySpaced = field.layout_config?.evenlySpaced || false;
        return (
          <RadioGroup value={value} onValueChange={(val) => handleInputChange(field.id, val)}>
            <div className={radioLayout === 'horizontal' ? `flex flex-wrap gap-4 ${radioEvenlySpaced ? 'justify-between' : ''}` : 'space-y-3'}>
              {field.options?.filter((option: string | any) => typeof option === 'string' || option !== 'layout').map((option: string, index: number) => (
                <div key={index} className={`${radioLayout === 'horizontal' && radioLabelBelow ? 'flex flex-col items-center' : 'flex items-center space-x-2'} ${radioEvenlySpaced && radioLayout === 'horizontal' ? 'flex-1' : ''}`}>
                  <RadioGroupItem value={option} id={`${field.id}-${index}`} />
                  <Label htmlFor={`${field.id}-${index}`} className={radioLayout === 'horizontal' && radioLabelBelow ? 'mt-2 text-center' : ''}>{option}</Label>
                </div>
              ))}
            </div>
          </RadioGroup>
        );

      case 'checkbox':
        const checkboxLayout = field.layout_config?.layout || 'vertical';
        const checkboxLabelBelow = field.layout_config?.labelBelow || false;
        const checkboxEvenlySpaced = field.layout_config?.evenlySpaced || false;
        return (
          <div className={checkboxLayout === 'horizontal' ? `flex flex-wrap gap-4 ${checkboxEvenlySpaced ? 'justify-between' : ''}` : 'space-y-2'}>
            {field.options?.filter((option: string | any) => typeof option === 'string' || option !== 'layout').map((option: string, index: number) => (
              <div key={index} className={`${checkboxLayout === 'horizontal' && checkboxLabelBelow ? 'flex flex-col items-center' : 'flex items-center space-x-2'} ${checkboxEvenlySpaced && checkboxLayout === 'horizontal' ? 'flex-1' : ''}`}>
                <Checkbox
                  id={`${field.id}-${index}`}
                  checked={Array.isArray(value) && value.includes(option)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      handleInputChange(field.id, [...(Array.isArray(value) ? value : []), option]);
                    } else {
                      handleInputChange(field.id, Array.isArray(value) ? value.filter((v: string) => v !== option) : []);
                    }
                  }}
                />
                <Label htmlFor={`${field.id}-${index}`} className={checkboxLayout === 'horizontal' && checkboxLabelBelow ? 'mt-2 text-center' : ''}>{option}</Label>
              </div>
            ))}
          </div>
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.is_required}
          />
        );

      case 'av_check':
        return (
          <AVCheckField
            field={field}
            value={formData[field.id]}
            onChange={(v) => handleInputChange(field.id, v)}
          />
        );

      case 'connection_check':
        return (
          <ConnectionCheckField
            field={field}
            value={formData[field.id]}
            onChange={(v) => handleInputChange(field.id, v)}
          />
        );

      case 'scheduler': {
        const ready = shouldShowSchedulerField(field, formData, form?.fields || []);
        if (!ready) {
          return (
            <div className="rounded-xl border border-dashed border-muted-foreground/40 bg-muted/20 p-6 text-center">
              <p className="text-sm font-medium text-foreground">Scheduling locked</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Complete all the questions above — including the camera/mic and
                connection checks — to unlock interview scheduling.
              </p>
            </div>
          );
        }
        return (
          <SchedulerField
            field={field}
            value={formData[field.id]}
            onChange={(v) => handleInputChange(field.id, v)}
          />
        );
      }

      default:
        return (
          <Input
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.is_required}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>Error</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate(`/anonymous/${accessToken}`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/anonymous/${accessToken}`)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-foreground">
                {form?.title}
              </h1>
              <p className="text-sm text-muted-foreground">
                {showUserInfoForm 
                  ? 'Please provide your contact information first'
                  : 'Fill out the form below to submit your response'
                }
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <Card className="max-w-3xl mx-auto">
          <CardHeader>
            {!showUserInfoForm && renderAboveTitleImages()}
            <CardTitle>
              {showUserInfoForm ? 'Contact Information' : form?.title}
            </CardTitle>
            {showUserInfoForm ? (
              <p className="text-muted-foreground">
                We need your contact information to process your form submission.
              </p>
            ) : (
              form?.description && (
                <p className="text-muted-foreground">{form.description}</p>
              )
            )}
          </CardHeader>
          <CardContent>
            {showUserInfoForm ? (
              <form onSubmit={handleUserInfoSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">
                      First Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="first_name"
                      type="text"
                      value={userInfo.first_name}
                      onChange={(e) => setUserInfo(prev => ({ ...prev, first_name: e.target.value }))}
                      placeholder="Enter your first name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">
                      Last Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="last_name"
                      type="text"
                      value={userInfo.last_name}
                      onChange={(e) => setUserInfo(prev => ({ ...prev, last_name: e.target.value }))}
                      placeholder="Enter your last name"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={userInfo.email}
                    onChange={(e) => setUserInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter your email address"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={userInfo.phone}
                    onChange={(e) => setUserInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Enter your phone number"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="institute">
                      Institute <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="institute"
                      type="text"
                      value={userInfo.institute}
                      onChange={(e) => setUserInfo(prev => ({ ...prev, institute: e.target.value }))}
                      placeholder="Enter your institute or organization"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role">
                      Role <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="role"
                      type="text"
                      value={userInfo.role}
                      onChange={(e) => setUserInfo(prev => ({ ...prev, role: e.target.value }))}
                      placeholder="Enter your role or position"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">
                      Country <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={userInfo.country}
                      onValueChange={(value) => {
                        setUserInfo(prev => ({ 
                          ...prev, 
                          country: value,
                          region: '',
                          city: ''
                        }));
                      }}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {locationData?.countries.map(([code, name]) => (
                          <SelectItem key={code} value={code}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="region">
                      Region <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={userInfo.region}
                      onValueChange={(value) => {
                        setUserInfo(prev => ({ 
                          ...prev, 
                          region: value,
                          city: ''
                        }));
                      }}
                      disabled={!userInfo.country}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {locationData?.regions.map((region) => (
                          <SelectItem key={region} value={region}>
                            {region}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">
                      City <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={userInfo.city}
                      onValueChange={(value) => {
                        setUserInfo(prev => ({ ...prev, city: value }));
                      }}
                      disabled={!userInfo.region}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select city" />
                      </SelectTrigger>
                      <SelectContent>
                        {userInfo.region && locationData?.cities_by_region[userInfo.region]?.map((city) => (
                          <SelectItem key={city} value={city}>
                            {city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(`/anonymous/${accessToken}`)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    Continue to Form
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-muted/50 rounded-lg p-4 mb-6">
                  <h3 className="font-medium text-foreground mb-2">Contact Information</h3>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>Name:</strong> {userInfo.first_name} {userInfo.last_name}</p>
                    <p><strong>Email:</strong> {userInfo.email}</p>
                    <p><strong>Phone:</strong> {userInfo.phone}</p>
                    {userInfo.institute && <p><strong>Institute:</strong> {userInfo.institute}</p>}
                    {userInfo.role && <p><strong>Role:</strong> {userInfo.role}</p>}
                    {userInfo.country && (
                      <p><strong>Location:</strong> {locationData?.countries.find(([code]) => code === userInfo.country)?.[1]}
                        {userInfo.region && `, ${userInfo.region}`}
                        {userInfo.city && `, ${userInfo.city}`}
                      </p>
                    )}
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      onClick={() => setShowUserInfoForm(true)}
                      className="p-0 h-auto text-xs"
                    >
                      Edit contact information
                    </Button>
                  </div>
                </div>

                {form?.fields?.filter(field => !(isImageField(field) && field.options?.aboveTitle === true)).map((field) => (
                  <div key={field.id} className="space-y-2">
                    <Label htmlFor={field.id}>
                      {field.label}
                      {field.is_required && (
                        <span className="text-destructive ml-1">*</span>
                      )}
                    </Label>
                    {renderField(field)}
                  </div>
                ))}

                <div className="flex justify-end space-x-3 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowUserInfoForm(true)}
                  >
                    Back to Contact Info
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-background mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Form
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AnonymousFormFillForm;